import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

export interface HelpPage {
  route: string
  file: string
  title: string
  icon: string
  order: number
}

interface HelpIndex {
  pages: HelpPage[]
}

export const useHelpStore = defineStore('help', () => {
  // ============================================
  // State
  // ============================================

  const isOpen = ref(false)
  const currentRouteName = ref('')
  const helpIndex = ref<HelpIndex | null>(null)
  const currentContent = ref('')
  const isLoading = ref(false)
  const showNavigation = ref(false)

  // ============================================
  // Getters
  // ============================================

  const currentPageTitle = computed(() => {
    if (!helpIndex.value || !currentRouteName.value) return ''
    const page = helpIndex.value.pages.find(p => p.route === currentRouteName.value)
    return page?.title || ''
  })

  const sortedPages = computed(() => {
    if (!helpIndex.value) return []
    return [...helpIndex.value.pages].sort((a, b) => a.order - b.order)
  })

  // ============================================
  // Actions
  // ============================================

  async function loadIndex(): Promise<void> {
    if (helpIndex.value) return
    try {
      const response = await fetch('/help/help-index.json', { cache: 'no-cache' })
      if (response.ok) {
        helpIndex.value = await response.json()
      }
    } catch (err) {
      console.error('[HelpStore] Failed to load help index:', err)
    }
  }

  async function loadContent(routeName?: string): Promise<void> {
    const target = routeName || currentRouteName.value
    if (!target) {
      currentContent.value = ''
      return
    }

    await loadIndex()

    const page = helpIndex.value?.pages.find(p => p.route === target)
    if (!page) {
      currentContent.value = ''
      return
    }

    isLoading.value = true
    try {
      const response = await fetch(`/help/${page.file}`, { cache: 'no-cache' })
      if (response.ok) {
        currentContent.value = await response.text()
      } else {
        currentContent.value = ''
      }
    } catch (err) {
      console.error('[HelpStore] Failed to load help content:', err)
      currentContent.value = ''
    } finally {
      isLoading.value = false
    }
  }

  async function open(routeName: string): Promise<void> {
    currentRouteName.value = routeName
    isOpen.value = true
    await loadContent()
  }

  function close(): void {
    isOpen.value = false
    showNavigation.value = false
  }

  async function toggle(routeName: string): Promise<void> {
    if (isOpen.value) {
      close()
    } else {
      await open(routeName)
    }
  }

  async function navigateTo(routeName: string): Promise<void> {
    currentRouteName.value = routeName
    await loadContent()
    showNavigation.value = false
  }

  function toggleNavigation(): void {
    showNavigation.value = !showNavigation.value
  }

  return {
    // State
    isOpen,
    currentRouteName,
    helpIndex,
    currentContent,
    isLoading,
    showNavigation,

    // Getters
    currentPageTitle,
    sortedPages,

    // Actions
    open,
    close,
    toggle,
    loadIndex,
    loadContent,
    navigateTo,
    toggleNavigation
  }
})
